import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { CampaignPagePreview } from '@/components/campaign-page-preview';
export const Route = createFileRoute('/_layout/blog/page-design-themes/')({
    component: PageDesignThemesPost,
    head: () => ({
        meta: [
            { title: '35 themes × 42 animated backgrounds — Adora page-design system' },
            {
                name: 'description',
                content: 'Inside Adora’s page-design system: 35 visual themes, 42 animated backgrounds, mode-aware rendering, and a Pro tier that unlocks the WOW patterns. How it works and why we separated theme from animation.',
            },
            { property: 'og:title', content: '35 themes × 42 animated backgrounds — Adora page-design system' },
            {
                property: 'og:description',
                content: 'Inside Adora’s page-design system: 35 visual themes, 42 animated backgrounds, mode-aware rendering, and a Pro tier that unlocks the WOW patterns.',
            },
        ],
    }),
});
function PageDesignThemesPost() {
    return (<article className="mx-auto max-w-3xl px-6 md:px-8 py-12">
            <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-3.5 w-3.5"/>
                All posts
            </Link>

            <header className="mt-6 space-y-3">
                <p className="text-xs uppercase tracking-wide text-primary">Product · 2026-05-08</p>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                    35 themes × 42 animated backgrounds: inside Adora’s page-design system
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                    Every QR scan lands on a public page. Until that page looks like <em>your</em> brand — not a generic
                    redirect — the QR is leaking attention. Here’s how we built a design system that ships 35 themes and
                    42 animated backgrounds, and why we treat them as two separate axes.
                </p>
            </header>

            <div className="prose prose-neutral dark:prose-invert mt-10 max-w-none">
                <h2>Why theme and animation are separate axes</h2>
                <p>
                    Most landing-page builders couple the two: pick &ldquo;Synthwave&rdquo;, get the synthwave layout{' '}
                    <em>and</em> the synthwave animation, take it or leave it. Adora doesn&rsquo;t. A theme defines the
                    static look — palette, typography, layout variant, radii, shadow. An animated background is its
                    own slot, sourced from a separate catalogue of 42 patterns. Each campaign stores them as two
                    independent choices, so swapping one never disturbs the other.
                </p>
                <p>
                    The reason is product, not just code hygiene. Aurora-Borealis as a theme reads{' '}
                    <em>calm and premium</em>. The Aurora animation reinforces that. But maybe you want the Aurora theme
                    with Fireworks animation for a New-Year promo, or the Industrial theme with Blueprint pattern for a
                    B2B launch. Decoupling lets the brand colour/typography stay constant while the motion follows the
                    moment.
                </p>

                <h2>What ships in the box</h2>
                <p>
                    35 themes across 5 design families — minimal / industrial / nature (sakura, forest, ocean) /
                    futurist (neon, cyberpunk, synthwave, glassmorphism) / playful (developer, neo-brutal, fireworks).
                    15 are free; 20 are Solo+. 42 animated backgrounds across 4 groups — flat, ambient (aurora, mesh,
                    waves, plasma, vortex, galaxy …), geometric (grid, blueprint, scanlines, circuit …), particles
                    (firefly, fireworks, snow, petals, confetti …). 15 free; 27 Solo+.
                </p>

                <h2>Live previews</h2>
                <p className="text-sm text-muted-foreground">
                    Each tile below is the real public route — same CSS, same animation timeline as production. No
                    mockups. The preview route never records a scan, so previews don’t pollute your analytics.
                </p>
                <div className="not-prose grid gap-8 sm:grid-cols-2 lg:grid-cols-3 items-end justify-items-center my-10">
                    <CampaignPagePreview design="aurora-borealis" pattern="aurora" mode="showroom" caption="Aurora Borealis · Aurora" size="sm"/>
                    <CampaignPagePreview design="cyberpunk" pattern="glitch" mode="classic" caption="Cyberpunk · Glitch" size="sm"/>
                    <CampaignPagePreview design="industrial" pattern="blueprint" mode="showroom" caption="Industrial · Blueprint" size="sm"/>
                    <CampaignPagePreview design="sakura" pattern="petals" mode="feedback" caption="Sakura · Petals" size="sm"/>
                    <CampaignPagePreview design="neo-brutal" pattern="neo-brutal" mode="classic" caption="Neo-Brutal · Blocks" size="sm"/>
                    <CampaignPagePreview design="developer" pattern="floating-code" mode="showroom" caption="Developer · Floating Code" size="sm"/>
                </div>

                <h2>Mode-aware rendering</h2>
                <p>
                    A theme isn’t a static template — it’s a <em>token bundle</em>. The same Aurora-Borealis theme
                    renders three completely different layouts depending on the campaign’s mode:
                </p>
                <ul>
                    <li>
                        <strong>Showroom</strong> — a card-based product gallery. Each tracking link becomes a tile with
                        image, price, and CTA. Tap → modal with details.
                    </li>
                    <li>
                        <strong>Classic</strong> — the linktree-style stacked CTA list. Each tracking link is one big,
                        thumb-friendly button.
                    </li>
                    <li>
                        <strong>Feedback</strong> — a rating + comment form, mobile-first, sticky submit, 16px+ font
                        sizes (no iOS zoom-on-focus).
                    </li>
                </ul>
                <p>
                    The theme picks <em>colours and typography</em>; the mode picks <em>layout</em>. They compose. So
                    "Sakura · Feedback" and "Sakura · Showroom" share a palette but render structurally different pages.
                </p>

                <h2>Random animation pool</h2>
                <p>
                    Solo+ campaigns can opt into a <strong>random background</strong> instead of pinning one. On every
                    page render, Adora picks from a workspace-defined pool — so a single QR scan can show snow today,
                    confetti tomorrow, fireworks next week. Useful for evergreen QRs (table tents, shop windows) where
                    the same physical surface gets repeat impressions and you want the page to feel alive.
                </p>

                <h2>Pro gating, server-side</h2>
                <p>
                    The Pro-only marker on themes and patterns isn&rsquo;t just a UI hint. The picker hides locked
                    options, and every save is re-checked against your workspace&rsquo;s plan on the server too.
                    Bypassing the picker (DevTools, hand-crafted API calls) doesn&rsquo;t bypass the gate.
                </p>

                <h2>Customizing your campaign</h2>
                <p>
                    Each campaign has a <strong>Customize</strong> tab in the console — a dedicated sub-route, not a
                    modal. Pick a theme, pick a pattern, toggle random, preview live before you save. Settings persist
                    on the campaign itself, so duplicating a campaign duplicates the look too.
                </p>

                <h2>What’s next</h2>
                <ul>
                    <li>Workspace-saved themes (the "Save as theme" Pro feature, tokenizing your brand once).</li>
                    <li>Per-time-window animation rotation (a different pattern by hour or day-of-week).</li>
                    <li>Custom font upload for Team plan.</li>
                    <li>More patterns. Always more patterns.</li>
                </ul>
            </div>

            <footer className="mt-12 border-t pt-6 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-3.5 w-3.5"/>
                    All posts
                </Link>
                <Link to="/sign-up" search={{ redirect: '/console' }} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90">
                    Try it free
                    <ArrowRight className="h-4 w-4"/>
                </Link>
            </footer>
        </article>);
}
